import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { LacakHalaman, catatPeran } from "@/lib/analytics";
import LoginPage from "@/pages/LoginPage";
import RegisterSupplierPage from "@/pages/RegisterSupplierPage";
import AppLayout from "@/pages/AppLayout";
import DashboardPage from "@/pages/DashboardPage";
import PetaPage from "@/pages/PetaPage";
import ProdukPage from "@/pages/ProdukPage";
import PurchaseOrderListPage from "@/pages/PurchaseOrderListPage";
import PurchaseOrderDetailPage from "@/pages/PurchaseOrderDetailPage";
import BuatPOPage from "@/pages/BuatPOPage";
import SuppliersPage from "@/pages/SuppliersPage";
import SupplierDetailPage from "@/pages/SupplierDetailPage";
import ProfilSupplierPage from "@/pages/ProfilSupplierPage";
import ProfilInstansiPage from "@/pages/ProfilInstansiPage";
import UserManagementPage from "@/pages/UserManagementPage";
import LaporanPage from "@/pages/LaporanPage";
import ForecastingPage from "@/pages/ForecastingPage";
import TicketListPage from "@/pages/TicketListPage";
import BuatTicketPage from "@/pages/BuatTicketPage";
import TicketDetailPage from "@/pages/TicketDetailPage";
import HeatmapStokPage from "@/pages/HeatmapStokPage";
import VerifikasiSupplierPage from "@/pages/VerifikasiSupplierPage";
import MasterDataPage from "@/pages/MasterDataPage";
import BackupDatabasePage from "@/pages/BackupDatabasePage";

// Pelacakan Google Analytics: kunjungan halaman + role pengguna (tanpa data pribadi).
function Analitik() {
  const { user } = useAuth();
  useEffect(() => { catatPeran(user?.role); }, [user?.role]);
  return <LacakHalaman />;
}

function RoleGate({ roles, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function FeatureGate({ feature, children }) {
  const { features } = useAuth();
  if (!features[feature]) return <Navigate to="/dashboard" replace />;
  return children;
}

// Supplier yang belum diverifikasi tidak boleh membuka Kelola Produk dan
// Pesanan Masuk, termasuk bila URL-nya diketik langsung.
function SupplierVerifiedGate({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "Supplier" && user.supplier_verified === false) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AdminGate({ children }) {
  const { user } = useAuth();
  if (!user || !user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Analitik />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/daftar-supplier" element={<RegisterSupplierPage />} />
            <Route element={<AppLayout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/peta" element={
                <RoleGate roles={["KPPG", "SPPG", "TPID", "Pemda", "Pemprov"]}><PetaPage /></RoleGate>
              } />
              <Route path="/monitoring-komoditas" element={
                <RoleGate roles={["KPPG", "TPID"]}><HeatmapStokPage /></RoleGate>
              } />
              <Route path="/heatmap-stok" element={<Navigate to="/monitoring-komoditas" replace />} />
              <Route path="/suppliers" element={
                <RoleGate roles={["KPPG", "SPPG", "TPID", "Pemda", "Pemprov"]}><SuppliersPage /></RoleGate>
              } />
              <Route path="/suppliers/:id" element={
                <RoleGate roles={["KPPG", "SPPG", "TPID", "Pemda", "Pemprov"]}><SupplierDetailPage /></RoleGate>
              } />
              <Route path="/purchase-orders" element={<PurchaseOrderListPage />} />
              <Route path="/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
              <Route path="/po-buat" element={
                <RoleGate roles={["SPPG"]}><BuatPOPage /></RoleGate>
              } />
              <Route path="/po-edit/:id" element={
                <RoleGate roles={["SPPG"]}><BuatPOPage /></RoleGate>
              } />
              <Route path="/po-masuk" element={
                <RoleGate roles={["Supplier"]}><SupplierVerifiedGate><PurchaseOrderListPage /></SupplierVerifiedGate></RoleGate>
              } />
              <Route path="/produk" element={
                <RoleGate roles={["Supplier"]}><SupplierVerifiedGate><ProdukPage /></SupplierVerifiedGate></RoleGate>
              } />
              <Route path="/profil-supplier" element={
                <RoleGate roles={["Supplier"]}><ProfilSupplierPage /></RoleGate>
              } />
              <Route path="/profil-sppg" element={
                <RoleGate roles={["SPPG"]}><ProfilInstansiPage /></RoleGate>
              } />
              <Route path="/profil-pemda" element={
                <RoleGate roles={["Pemda"]}><ProfilInstansiPage /></RoleGate>
              } />
              <Route path="/profil-pemprov" element={
                <RoleGate roles={["Pemprov"]}><ProfilInstansiPage /></RoleGate>
              } />
              <Route path="/users" element={
                <RoleGate roles={["KPPG"]}><UserManagementPage /></RoleGate>
              } />
              <Route path="/verifikasi-supplier" element={
                <RoleGate roles={["KPPG", "Pemda"]}><VerifikasiSupplierPage /></RoleGate>
              } />
              <Route path="/laporan" element={
                <RoleGate roles={["KPPG", "TPID", "Pemda", "Pemprov"]}><LaporanPage /></RoleGate>
              } />
              <Route path="/forecasting" element={
                <FeatureGate feature="forecasting">
                  <RoleGate roles={["KPPG", "TPID"]}><ForecastingPage /></RoleGate>
                </FeatureGate>
              } />
              <Route path="/tickets" element={<TicketListPage />} />
              <Route path="/tickets/new" element={<BuatTicketPage />} />
              <Route path="/tickets/:id" element={<TicketDetailPage />} />
              <Route path="/master-data" element={
                <AdminGate><MasterDataPage /></AdminGate>
              } />
              <Route path="/backup-database" element={
                <AdminGate><BackupDatabasePage /></AdminGate>
              } />
            </Route>
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
